import React, { useState } from "react";
import { Link } from "react-router-dom";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";

// Sample data based on the provided CSV format
const statsData = [
  {
    season: "Career",
    avgMin: 33.7,
    fg: 31,
    fga: 59,
    fgPct: 0.53,
    threesMade: 14,
    threesAttempted: 27,
    threesPct: 0.52,
    ft: 10,
    fta: 12,
    ftPct: 0.83,
    offReb: 4,
    defReb: 6,
    totReb: 10,
    avgReb: 3.3,
    assists: 14,
    turnovers: 9,
    blocks: 0,
    steals: 4,
    points: 86,
    avgPts: 28.7,
    astToTurnoverRatio: 1.56,
    trueShootingPct: 0.67,
    effectiveFgPct: 0.64,
    estPossessionsPerGame: 31.18,
    ppp: 0.92,
    tpp: 0.21,
    totalReboundPercentage: 21,
    offensiveReboundPercentage: 39,
    defensiveReboundPercentage: 61,
    assistedFG: 52,
    turnoverPercentage: 16,
    blockPercentage: 3,
    stealPercentage: 7,
    usageRate: 21,
    offensiveRating: 92,
    defensiveRating: 88,
  },
  {
    season: "2023-24",
    avgMin: 35.2,
    fg: 34,
    fga: 62,
    fgPct: 0.55,
    threesMade: 16,
    threesAttempted: 29,
    threesPct: 0.55,
    ft: 12,
    fta: 14,
    ftPct: 0.86,
    offReb: 5,
    defReb: 7,
    totReb: 12,
    avgReb: 4.0,
    assists: 16,
    turnovers: 8,
    blocks: 1,
    steals: 5,
    points: 96,
    avgPts: 32.0,
    astToTurnoverRatio: 2.0,
    trueShootingPct: 0.71,
    effectiveFgPct: 0.68,
    estPossessionsPerGame: 32.5,
    ppp: 0.98,
  },
  {
    season: "2022-23",
    avgMin: 32.8,
    fg: 29,
    fga: 57,
    fgPct: 0.51,
    threesMade: 13,
    threesAttempted: 26,
    threesPct: 0.5,
    ft: 9,
    fta: 11,
    ftPct: 0.82,
    offReb: 4,
    defReb: 6,
    totReb: 10,
    avgReb: 3.3,
    assists: 13,
    turnovers: 10,
    blocks: 0,
    steals: 3,
    points: 80,
    avgPts: 26.7,
    astToTurnoverRatio: 1.3,
    trueShootingPct: 0.65,
    effectiveFgPct: 0.62,
    estPossessionsPerGame: 30.5,
    ppp: 0.88,
  },
  {
    season: "2021-22",
    avgMin: 31.5,
    fg: 27,
    fga: 54,
    fgPct: 0.5,
    threesMade: 11,
    threesAttempted: 24,
    threesPct: 0.46,
    ft: 8,
    fta: 10,
    ftPct: 0.8,
    offReb: 3,
    defReb: 5,
    totReb: 8,
    avgReb: 2.7,
    assists: 12,
    turnovers: 9,
    blocks: 0,
    steals: 3,
    points: 73,
    avgPts: 24.3,
    astToTurnoverRatio: 1.33,
    trueShootingPct: 0.63,
    effectiveFgPct: 0.6,
    estPossessionsPerGame: 29.2,
    ppp: 0.83,
  },
];

const StatsTab = ({ player, stats }) => {
  // Add state to track the selected season index (0 = career stats)
  const [selectedSeasonIndex, setSelectedSeasonIndex] = useState(0);

  // Get the stats for the selected season
  const seasonStats = statsData[selectedSeasonIndex];

  const VideoIcon = () => (
    <span className="player-game-stats-video-icon">
      <VideocamRoundedIcon sx={{ color: "#4CAF50", fontSize: "2rem" }} />
    </span>
  );

  const TitleWithVideo = ({ title }) => (
    <h3
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: "1rem",
      }}
    >
      <span>{title}</span>
      <VideoIcon />
    </h3>
  );

  // Create functions to handle navigation
  const handleSeasonChange = (index) => {
    setSelectedSeasonIndex(index);
  };

  const goToPreviousSeason = () => {
    setSelectedSeasonIndex((prev) =>
      prev === 0 ? statsData.length - 1 : prev - 1
    );
  };

  const goToNextSeason = () => {
    setSelectedSeasonIndex((prev) =>
      prev === statsData.length - 1 ? 0 : prev + 1
    );
  };

  // Get the title based on selected season
  const getSeasonTitle = () => {
    if (selectedSeasonIndex === 0) {
      return "Career Statistics";
    } else {
      return `${statsData[selectedSeasonIndex].season} Statistics`;
    }
  };

  // Get the display text for the current selection
  const getCurrentSeasonDisplay = () => {
    return selectedSeasonIndex === 0
      ? "Career"
      : statsData[selectedSeasonIndex].season;
  };

  return (
    <div className="stats-tab">
      {/* Season selector with arrows */}
      <div className="stats-tab-season-selector">
        <button
          className="stats-tab-season-nav-button"
          onClick={goToPreviousSeason}
          aria-label="Previous season"
        >
          &#9664;
        </button>

        <div className="stats-tab-season-display">
          {getCurrentSeasonDisplay()}
        </div>

        <button
          className="stats-tab-season-nav-button"
          onClick={goToNextSeason}
          aria-label="Next season"
        >
          &#9654;
        </button>
      </div>

      {/* <h2 style={{ textAlign: "center" }}>{getSeasonTitle()}</h2> */}

      <div className="player-career-cards">
        <div className="player-career-card">
          <h3>Minutes</h3>
          <div className="player-career-card-value">{seasonStats.avgMin}</div>
          <div className="player-career-card-label">Average Minutes</div>
        </div>

        <Link
          to={`/game-footage?type=fgm&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Field Goals" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.fg}</div>
                <span>FGM</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.fga}</div>
                <span>FGA</span>
              </div>
              <div className="player-career-card-stat">
                <div>{(seasonStats.fgPct * 100).toFixed(1)}</div>
                <span>FG%</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to={`/game-footage?type=3ptm&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Three Point Shooting" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.threesMade}</div>
                <span>3PTM</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.threesAttempted}</div>
                <span>3PTA</span>
              </div>
              <div className="player-career-card-stat">
                <div>{(seasonStats.threesPct * 100).toFixed(1)}</div>
                <span>3PT%</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to={`/game-footage?type=ftm&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Free Throws" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.ft}</div>
                <span>FTM</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.fta}</div>
                <span>FTA</span>
              </div>
              <div className="player-career-card-stat">
                <div>{(seasonStats.ftPct * 100).toFixed(1)}</div>
                <span>FT%</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to={`/game-footage?type=rebounds&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Rebounds" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.offReb}</div>
                <span>OFF</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.defReb}</div>
                <span>DEF</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.totReb}</div>
                <span>TOTAL</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.avgReb}</div>
                <span>AVG</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to={`/game-footage?type=assists&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Assists" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.assists}</div>
                <span>AST</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.astToTurnoverRatio}</div>
                <span>AST/TO</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.assistedFG}</div>
                <span>AST FG%</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to={`/game-footage?type=turnovers&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Turnovers" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.turnovers}</div>
                <span>TO</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.turnoverPercentage}</div>
                <span>TO%</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to={`/game-footage?type=blocks&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Blocks" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.blocks}</div>
                <span>BLK</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.blockPercentage}</div>
                <span>BLK%</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to={`/game-footage?type=steals&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Steals" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.steals}</div>
                <span>STL</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.stealPercentage}</div>
                <span>STL%</span>
              </div>
            </div>
          </div>
        </Link>

        <Link
          to={`/game-footage?type=points&player_id=${player.player_id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div className="player-career-card">
            <TitleWithVideo title="Scoring" />
            <div className="player-career-card-stats">
              <div className="player-career-card-stat">
                <div>{seasonStats.points}</div>
                <span>PTS</span>
              </div>
              <div className="player-career-card-stat">
                <div>{seasonStats.avgPts}</div>
                <span>AVG</span>
              </div>
              <div className="player-career-card-stat">
                <div>{(seasonStats.trueShootingPct * 100).toFixed(1)}</div>
                <span>TS%</span>
              </div>
              <div className="player-career-card-stat">
                <div>{(seasonStats.effectiveFgPct * 100).toFixed(1)}</div>
                <span>EFG%</span>
              </div>
            </div>
          </div>
        </Link>

        <div className="player-career-card">
          <h3>Possessions</h3>
          <div className="player-career-card-stats">
            <div className="player-career-card-stat">
              <div>{seasonStats.estPossessionsPerGame}</div>
              <span>POSS/G</span>
            </div>
            <div className="player-career-card-stat">
              <div>{seasonStats.ppp}</div>
              <span>PPP</span>
            </div>
          </div>
        </div>
        <div className="player-career-card">
          <h3>Usage Rate</h3>
          <div className="player-career-card-stats">
            <div className="player-career-card-stat">
              <div>{seasonStats.usageRate}</div>
              <span>USG%</span>
            </div>
            <div className="player-career-card-stat">
              <div>{seasonStats.offensiveRating}</div>
              <span>OFFRtg</span>
            </div>
            <div className="player-career-card-stat">
              <div>{seasonStats.defensiveRating}</div>
              <span>DEFRtg</span>
            </div>
          </div>
        </div>
        <div className="player-career-card">
          <h3>Advanced Rebounding</h3>
          <div className="player-career-card-stats">
            <div className="player-career-card-stat">
              <div>{seasonStats.totalReboundPercentage}</div>
              <span>TRB%</span>
            </div>
            <div className="player-career-card-stat">
              <div>{seasonStats.offensiveReboundPercentage}</div>
              <span>OREB%</span>
            </div>
            <div className="player-career-card-stat">
              <div>{seasonStats.defensiveReboundPercentage}</div>
              <span>DREB%</span>
            </div>
          </div>
        </div>
      </div>

      <h2>Shot Chart</h2>
      <div className="shot-chart-container">
        <img
          style={{ width: "100%", height: "auto" }}
          src="/shotchart-advanced.jpeg"
          alt="Shot Chart"
        />
      </div>
    </div>
  );
};

export default StatsTab;
